import React from 'react';
import { usePageMeta } from '../hooks/usePageMeta';
import { Reveal } from '../components/ui-kit/Reveal';

const Block = ({ title, children, id }) => (
  <section id={id} className="mt-10 first:mt-0 scroll-mt-28">
    <Reveal>
      <h2 className="text-h3 text-ink">{title}</h2>
    </Reveal>
    <Reveal delay={60}>
      <div className="mt-3 text-body text-ink-muted space-y-3 max-w-2xl">{children}</div>
    </Reveal>
  </section>
);

const Todo = ({ children }) => (
  <span
    className="inline-block bg-yellow-100 text-yellow-900 border border-yellow-300 rounded px-1.5 py-0.5 text-[13px] font-mono"
    data-testid="tietosuoja-todo"
  >
    [TODO: {children}]
  </span>
);

const Tietosuoja = () => {
  usePageMeta({
    title: 'Agencive – Tietosuoja',
    description:
      'Miten Agencive käsittelee henkilötietoja verkkosivulla agencive.fi ja B2B-myyntiprospektien käsittelyssä.',
    path: '/tietosuoja',
  });

  // Update date is bumped only at production release. In preview / dev this
  // stays on the last officially approved production date so that visitors
  // do not see a moving date during iteration.
  const updated = '11.7.2026';

  return (
    <main className="bg-bg">
      <section className="pt-28 md:pt-32 pb-20 md:pb-24">
        <div className="container-page max-w-3xl">
          <Reveal>
            <span className="eyebrow text-ink-muted">Tietosuoja</span>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-5 text-h1 text-ink">Tietosuojaseloste</h1>
          </Reveal>
          <Reveal delay={140}>
            <p className="mt-5 text-body-lg text-ink-muted">
              Tämä seloste kuvaa henkilötietojen käsittelyn sivustolla agencive.fi sekä
              Agenciven B2B-myyntiprospektien käsittelyssä. Päivitetty {updated}.
            </p>
          </Reveal>

          <Block title="Rekisterinpitäjä" id="rekisterinpitaja">
            <p>
              <b>Agencive AI Oy</b>, Y-tunnus <b>3590217-8</b>.
            </p>
            <p>
              Postiosoite: <Todo>lisää yrityksen virallinen postiosoite</Todo>.
            </p>
            <p>
              Tietosuoja-asiat ja rekisteröidyn oikeuksiin liittyvät pyynnöt:{' '}
              <a className="text-brand hover:text-brand-hover underline" href="mailto:agencive.ai@gmail.com">
                agencive.ai@gmail.com
              </a>.
            </p>
            <p>
              Tietosuojavastaavaa <b>ei ole nimetty</b>. Kaikki tietosuoja-asiat käsitellään
              yllä olevan yhteyssähköpostin kautta.
            </p>
          </Block>

          <Block title="B2B-myyntiprospektien käsittely — tarkoitus">
            <p>
              Agencive AI Oy käsittelee yritysasiakkaiden yhteystietoja seuraaviin
              tarkoituksiin:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Yritysasiakkaiden tunnistaminen ja palvelun soveltuvuuden arviointi.</li>
              <li>Henkilökohtaisten B2B-yhteydenottojen valmistelu ja toteutus.</li>
              <li>Yhteydenottojen ja niihin liittyvien tietojen hallinta.</li>
              <li>Suoramarkkinointikieltojen noudattaminen ja rekisteröityjen vastustusten toteuttaminen.</li>
            </ul>
          </Block>

          <Block title="B2B-myyntiprospektien käsittelyn oikeusperuste">
            <p>
              B2B-prospektien henkilötietojen käsittelyn suunniteltu käsittelyperuste on
              <b> GDPR 6 artiklan 1 kohdan f alakohdan</b> mukainen oikeutettu etu. Oikeutettu
              etu on Agenciven liiketoiminnan kehittäminen ja relevantti B2B-suoramarkkinointi
              yrityspäättäjille, joiden rooli liittyy tarjoamaamme palveluun.
            </p>
            <p>
              Ennen käsittelyn ja yhteydenottojen aloittamista Agencive dokumentoi ja
              hyväksyy käsittelyä koskevan tasapainotestin (LIA-B2B-001). Käsittely
              rajataan roolinsa perusteella relevantteihin yrityspäättäjiin, ja
              rekisteröidyn edut, perusoikeudet ja vapaudet otetaan huomioon jokaisessa
              käsittelyvaiheessa.
            </p>
          </Block>

          <Block title="Sähköisen suoramarkkinoinnin sallittavuus">
            <p>
              GDPR:n mukainen henkilötietojen käsittelyperuste ei itsessään anna lupaa
              lähettää sähköistä suoramarkkinointia. Sähköisten yhteydenottojen
              sallittavuus arvioidaan erikseen sähköisen viestinnän palveluista annetun
              lain perusteella. Yrityksen yleisiin osoitteisiin kohdistuvissa viesteissä
              noudatetaan lain 202–203 §:ää. Luonnollisen henkilön osoitteeksi
              katsottavaan nimettyyn työosoitteeseen ei lähetetä sähköistä
              suoramarkkinointia ilman suostumusta tai muuta erikseen varmennettua
              lainmukaista perustetta.
            </p>
            <p>Jokaisessa markkinointiviestissä on:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>selkeä tieto viestin kaupallisesta tarkoituksesta,</li>
              <li>Agencive AI Oy lähettäjänä,</li>
              <li>toimiva vastausosoite,</li>
              <li>helppo ja maksuton kielto-ohje sekä</li>
              <li>
                linkki tämän selosteen vastustamisoikeutta koskevaan kohtaan:{' '}
                <a className="text-brand hover:text-brand-hover underline" href="/tietosuoja#vastustamisoikeus">
                  /tietosuoja#vastustamisoikeus
                </a>.
              </li>
            </ul>
          </Block>

          <Block title="Vastustamisoikeus ja suoramarkkinointikielto" id="vastustamisoikeus">
            <div
              className="rounded-2xl bg-ink text-white p-5 md:p-6 border border-ink shadow-md"
              data-testid="objection-callout"
            >
              <p className="text-[15.5px] leading-relaxed">
                <b>Voit kieltää suoramarkkinoinnin milloin tahansa maksutta</b> lähettämällä
                viestin osoitteeseen{' '}
                <a className="underline hover:text-white/90" href="mailto:agencive.ai@gmail.com">
                  agencive.ai@gmail.com
                </a>. Kiellon jälkeen henkilötietojasi ei enää käsitellä
                suoramarkkinointiin. Säilytämme vain kiellon noudattamiseksi välttämättömän
                estotiedon.
              </p>
            </div>
            <p>
              Rekisteröidyllä on lisäksi oikeus vastustaa oikeutettuun etuun perustuvaa
              muuta käsittelyä henkilökohtaiseen erityiseen tilanteeseensa liittyvillä
              perusteilla.
            </p>
          </Block>

          <Block title="Tietoryhmät">
            <p>Käsittelemme B2B-prospekteista seuraavia tietoja:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Nimi ja työrooli.</li>
              <li>Työnantajayritys ja yrityksen yhteystiedot.</li>
              <li>Yrityssähköposti ja työpuhelin.</li>
              <li>Yrityksen verkkotunnus ja julkiset verkkosivutiedot.</li>
              <li>
                Julkiset yritys- ja ammatilliset tiedot (esim. Y-tunnus, julkaistut roolit,
                julkiset ammattiprofiilit).
              </li>
              <li>
                Yhteydenotto-, lupa-, kielto- ja seurantatiedot (esim. lähetetyt viestit,
                vastustuskiellot, aikaleimat, tunnisteet ja viestien tila).
              </li>
            </ul>
            <p><b>Emme käsittele arkaluonteisia henkilötietoja</b> B2B-prospekteista.</p>
          </Block>

          <Block title="Tietolähteet ja ensimmäinen yhteydenotto">
            <p>Prospektitiedot kerätään ainoastaan seuraavista lähdekategorioista:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Yritysten omat julkiset verkkosivut.</li>
              <li>Muut julkiset yritys- ja ammatilliset lähteet (esim. julkiset yritysrekisterit ja tiedotteet).</li>
              <li>Rekisteröidyltä itseltään saadut tiedot.</li>
            </ul>
            <p>
              Jokaisesta prospektista tallennetaan <b>lähteen URL tai lähdekategoria</b> ja
              <b> tarkistuspäivä</b>. Sähköpostiosoitteita ei koskaan arvata eikä johdeta
              automaattisesti nimestä; ainoastaan julkisesti saatavilla olevia
              rooliperusteisia osoitteita käytetään.
            </p>
            <p>
              Ensimmäisessä henkilölle kohdistetussa yhteydenotossa kerromme:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Agenciven identiteetti,</li>
              <li>tietojen lähde tai lähdekategoria,</li>
              <li>
                linkki tähän selosteeseen (
                <a className="text-brand hover:text-brand-hover underline" href="/tietosuoja">
                  agencive.fi/tietosuoja
                </a>
                ) ja
              </li>
              <li>erillinen kielto-ohje.</li>
            </ul>
          </Block>

          <Block title="B2B-prospektikäsittelyn rajaukset">
            <p>
              Yhteydenoton pitää liittyä vastaanottajan työrooliin. Käytämme ensisijaisesti
              yrityksen yleistä yhteysosoitetta (esim. info@ tai myynti@). Nimetyn henkilön
              työosoitetta käytetään vain silloin, kun roolirelevanssi on varmistettu.
            </p>
            <p>
              <b>Emme käytä</b> henkilökohtaisia sähköpostiosoitteita, henkilökohtaisia
              puhelinnumeroita tai yksityisiä viestikanavia (esim. yksityinen WhatsApp tai
              yksityinen Facebook Messenger) ilman erillistä lainmukaista perustetta tai
              rekisteröidyn nimenomaista suostumusta.
            </p>
          </Block>

          <Block title="Yhteydenottolomake — käsittelyn peruste">
            <p>
              Kun lähetät sivuston yhteydenottolomakkeen, käsittelemme antamiasi tietoja
              alla mainituilla oikeusperusteilla:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <b>GDPR 6(1)(b)</b> — käyttäjän pyynnöstä tehtävät sopimusta edeltävät
                toimet, kun lomakkeella pyydetään esimerkiksi tarjousta, kartoituskeskustelua
                tai muuta palvelun arviointia.
              </li>
              <li>
                <b>GDPR 6(1)(f)</b> — muun yleisen yhteydenoton käsittely ja viestiin
                vastaaminen.
              </li>
            </ul>
            <p>
              Erillinen suostumus pyydetään ainoastaan analytiikka- ja markkinointi­
              teknologioihin (evästebanneri). Lomakkeen lähettäminen ei anna automaattisesti
              lupaa myöhempään sähköiseen suoramarkkinointiin.
            </p>
            <p>
              <b>Näkyvät lomakekentät:</b> nimi, yritys (vapaaehtoinen), sähköposti,
              puhelinnumero (vapaaehtoinen), kiinnostuksen kohde ja viestin sisältö.
              Lomakelähetys sisältää myös lähetysajan ja lomakkeen tapahtumatunnisteen
              (event_id).
            </p>
          </Block>

          <Block title="Käyttämämme palveluntarjoajat ja mahdolliset ETA:n ulkopuoliset siirrot">
            <p>
              Alla on kuvattu käytössä olevat palveluntarjoajat sekä sopimustilanne ja
              siirrot niiden osalta, joilta tiedot on todennettu. Vahvistamattomat kohdat
              on merkitty selkeästi.
            </p>
            <p><b>Emme myy henkilötietoja</b>.</p>
            <ul className="list-disc pl-6 space-y-4">
              <li>
                <b>Relevance AI</b> — agenttien ajoympäristö ja B2B-työnkulut.<br />
                Sopimusosapuoli: <Todo>vahvista juridinen sopimusosapuoli ja maa</Todo>.<br />
                Käyttötarkoitus: B2B-prospektien arviointi, viestiluonnokset ja työnkulut.<br />
                Käsittelyalue ja siirrot: <Todo>vahvista käsittelyalue ja mahdollinen ETA-siirto</Todo>.<br />
                DPA (henkilötietojen käsittelysopimus): <Todo>vahvista DPA:n allekirjoitus</Todo>.
              </li>
              <li>
                <b>Google Workspace / Gmail</b> — sähköposti ja siihen liittyvät työkalut.<br />
                Sopimusosapuoli: Google Ireland Limited (EU:ssa asuville käyttäjille).<br />
                Käyttötarkoitus: B2B-viestinnän lähetys, vastaanotto ja arkistointi.<br />
                Käsittelyalue: EU sekä Google Workspace -lisäasetusten mukaiset alueet.<br />
                Siirrot ETA:n ulkopuolelle: <Todo>vahvista Google Workspace -tiedon sijainti- ja siirtoasetukset</Todo>.
              </li>
              <li>
                <b>Discord</b> — sisäinen tiimikanava.<br />
                <b>Todellinen käyttö:</b> <Todo>
                  vahvista Discordin käyttötapaus — käytetäänkö pelkästään sisäisiin
                  tilailmoituksiin ja pseudonyymeihin tunnisteisiin, vai viedäänkö sinne
                  prospektien nimiä, sähköposteja tai viestisisältöjä. Jos jälkimmäinen,
                  tämä pitää erikseen hyväksyä.
                </Todo><br />
                Sopimusosapuoli: Discord Netherlands B.V. / Discord Inc. (
                <Todo>vahvista sopimusosapuoli</Todo>).
              </li>
              <li>
                <b>MongoDB</b> — lomake- ja prospektitietojen tietokantatallennus.<br />
                Sopimusosapuoli ja alue: <Todo>vahvista onko käytössä MongoDB Atlas, mikä alue ja mikä juridinen sopimusosapuoli</Todo>.<br />
                DPA: <Todo>vahvista DPA:n allekirjoitus</Todo>.
              </li>
              <li>
                <b>Resend</b> — lomakkeen ilmoitussähköpostien lähetys.<br />
                Sopimusosapuoli: Resend, Inc., USA.<br />
                Siirrot ETA:n ulkopuolelle: <Todo>vahvista käytössä oleva siirtoperuste (esim. SCC + täydentävät suojatoimet)</Todo>.
              </li>
              <li>
                <b>Meta Platforms Ireland</b> — Meta Pixel (selain) ja Meta Conversions API
                (palvelin) mainonnan mittaukseen. Sekä Pixel että CAPI ovat oletuksena pois
                käytöstä ja käynnistyvät vasta käyttäjän nimenomaisen markkinointi­
                suostumuksen jälkeen.<br />
                DPA: <Todo>vahvista Meta Business Tools -sopimus ja DPA</Todo>.
              </li>
              <li>
                <b>PostHog</b> — kävijäanalytiikka. Latautuu vasta analytiikkasuostumuksen
                jälkeen.<br />
                Sopimusosapuoli: PostHog Inc., USA. Käytössä on{' '}
                <Todo>vahvista PostHog EU Cloud -sijainti tai US Cloud + siirtoperuste</Todo>.
              </li>
              <li>
                <b>Voiceflow</b> — chat-agentti. Skripti ladataan vasta, kun avaat chatin
                &ldquo;Kysy agentilta&rdquo; -painikkeesta.<br />
                Sopimusosapuoli: Voiceflow, Inc., USA.<br />
                Siirrot ETA:n ulkopuolelle: <Todo>vahvista Voiceflow-siirtoperuste ja DPA</Todo>.
              </li>
              <li>
                <b>Emergent</b> — verkkosivun hosting-alusta.<br />
                Sopimusosapuoli, alue ja DPA: <Todo>vahvista juridinen sopimusosapuoli, käsittelyalue ja DPA</Todo>.
              </li>
              <li>
                <b>Cloudflare</b> — CDN ja tekninen suorituskykymittaus (Insights).
                Cloudflaren dokumentaation mukaan Insights ei käytä evästeitä eikä
                sormenjälkitunnistusta.<br />
                Sopimusosapuoli ja alue: <Todo>vahvista Cloudflare-sopimusosapuoli ja käsittelyalue</Todo>.
              </li>
            </ul>
            <p>
              Jos tietoja siirretään Euroopan talousalueen (ETA) ulkopuolelle, käytämme
              GDPR:n V luvun mukaista siirtoperustetta ja tarvittavia suojatoimia. Yllä
              olevat vahvistamattomat kohdat (TODO) päivitetään tuotantoon vasta, kun
              siirtoperuste on tarkistettu jokaisen palveluntarjoajan osalta.
            </p>
          </Block>

          <Block title="Meta Pixel, Meta Conversions API ja markkinointi">
            <p>
              Meta Pixel (selain) ja Meta Conversions API (palvelin) ovat oletuksena pois
              käytöstä. Ne käynnistyvät vasta, kun käyttäjä hyväksyy erikseen
              <b> Markkinointi</b>-suostumusluokan evästebannerissa.
            </p>
            <p>
              Ennen markkinointisuostumusta <b>Metalle ei lähetetä</b> nimeä, sähköpostia,
              puhelinnumeroa, hashattuja tunnisteita eikä Lead-tapahtumaa. Yhteydenottolomake
              toimii täysin ilman analytiikka- tai markkinointisuostumusta.
            </p>
            <p>
              Kun markkinointisuostumus on annettu ja lähetät lomakkeen, Meta CAPI:iin
              lähetetään antamasi tunnisteet (nimi, sähköposti, puhelinnumero)
              <b> hashattuina</b> (SHA-256). Hashattu tunniste on edelleen henkilötieto —
              hashaus vaikeuttaa uudelleentunnistusta, mutta ei tee tiedosta anonyymiä.
            </p>
          </Block>

          <Block title="Chat (Voiceflow)">
            <p>
              Voiceflow-widgetin skripti ei lataudu sivuston avautuessa. Se lataantuu vasta,
              kun avaat chatin painikkeesta. Voit käyttää sivustoa täysin ilman chatia,
              jolloin Voiceflowlle ei tehdä yhtään verkkopyyntöä.
            </p>
          </Block>

          <Block title="Evästeet ja suostumus">
            <p>Sivustolla on kolme evästekategoriaa:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li><b>Välttämättömät:</b> vaadittu sivuston toiminnalle (mm. suostumusvalintojen tallennus selaimeen).</li>
              <li><b>Analytiikka:</b> PostHog. Latautuu vasta suostumuksen jälkeen.</li>
              <li><b>Markkinointi:</b> Meta Pixel (selain) ja Meta Conversions API (palvelin). Latautuvat vasta suostumuksen jälkeen.</li>
            </ul>
            <p>
              Suostumus tallennetaan selaimen paikalliseen tallennustilaan (localStorage-avain
              <code> agencive-consent-v1</code>). Voit muuttaa valintaasi tai peruuttaa
              suostumuksesi milloin tahansa footerin <b>Evästeasetukset</b>-linkistä.
              Bannerissa &ldquo;Hylkää kaikki&rdquo; on yhtä helppo valita kuin
              &ldquo;Hyväksy kaikki&rdquo;.
            </p>
          </Block>

          <Block title="Säilytysajat">
            <p>
              <b>Yhteydenottolomakkeen tiedot:</b> säilytetään enintään <b>24 kuukautta</b>
              viimeisestä yhteydenpidosta, minkä jälkeen tiedot poistetaan tai
              anonymisoidaan. Kirjanpito- ja sopimusvelvoitteiden edellyttämät tiedot
              säilytetään lain edellyttämän ajan.
            </p>
            <p>
              <b>Aktiiviset B2B-prospektitiedot:</b> tarkistetaan säännöllisesti ja
              poistetaan viimeistään <b>12 kuukauden</b> kuluttua viimeisestä aidosta
              yhteydenotosta tai dokumentoidusta manuaalisesta käsittelystä. Pelkkä
              automaattinen järjestelmätarkistus ei aloita 12 kuukauden aikaa uudelleen.
              Tarpeettomat ja virheelliset prospektit poistetaan aikaisemmin.
            </p>
            <p>
              <b>Kielto- ja suppression-rekisteri:</b> säilytämme vain sen välttämättömän
              tiedon, joka tarvitaan saadun kiellon noudattamiseen jatkossa.
            </p>
          </Block>

          <Block title="Tekoälyagentit ja automaatio">
            <p>
              Agencive voi käyttää tekoälyagentteja yritysten soveltuvuuden arviointiin,
              julkisten yritystietojen kokoamiseen, viestiluonnosten laatimiseen ja
              työnkulkujen hallintaan. Tekoälyagentin tuottama arvio ei johda rekisteröityyn
              kohdistuvaan oikeusvaikutukseen tai vastaavaan merkittävään vaikutukseen.
              Ulkoiset yhteydenotot edellyttävät ihmisen määrittämiä sääntöjä ja
              hyväksyntämenettelyä.
            </p>
          </Block>

          <Block title="Tietoturva">
            <p>
              Käyttöoikeudet rajataan tarpeellisuusperiaatteen mukaisesti, ja käsittelystä
              pidetään lokia. Tietoja käsittelevät vain ne henkilöt, joiden työtehtävät sitä
              edellyttävät.
            </p>
          </Block>

          <Block title="Rekisteröidyn oikeudet">
            <p>Sinulla on oikeus:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Tarkastaa itseäsi koskevat tiedot.</li>
              <li>Pyytää tietojen oikaisua tai poistoa.</li>
              <li>Rajoittaa käsittelyä.</li>
              <li>
                Vastustaa käsittelyä (mukaan lukien suoramarkkinointi — ks.{' '}
                <a className="text-brand hover:text-brand-hover underline" href="/tietosuoja#vastustamisoikeus">
                  vastustamisoikeus
                </a>
                ).
              </li>
              <li>Peruuttaa antamasi suostumus milloin tahansa siltä osin kuin käsittely perustuu suostumukseen.</li>
              <li>
                Oikeus tietojen siirtämiseen järjestelmästä toiseen koskee vain niitä
                tilanteita, joissa GDPR:n edellytykset (mm. suostumus- tai sopimusperuste
                ja automatisoitu käsittely) täyttyvät.
              </li>
              <li>
                Tehdä valitus Tietosuojavaltuutetun toimistoon:{' '}
                <a
                  className="text-brand hover:text-brand-hover underline"
                  href="https://tietosuoja.fi/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://tietosuoja.fi/
                </a>
                .
              </li>
            </ul>
            <p>
              Pyynnöt osoitteeseen{' '}
              <a className="text-brand hover:text-brand-hover underline" href="mailto:agencive.ai@gmail.com">
                agencive.ai@gmail.com
              </a>. Vastaamme pyyntöihin lähtökohtaisesti <b>kuukauden kuluessa</b>.
              Henkilöllisyys voidaan tarvittaessa varmistaa ennen tietojen luovuttamista.
            </p>
          </Block>

          <Block title="Muutokset">
            <p>
              Voimme päivittää tätä selostetta toimintamme tai lainsäädännön muuttuessa.
              Ajantasaisin versio on aina tällä sivulla. Sivun päivityspäivä muuttuu
              vasta tuotantojulkaisun yhteydessä.
            </p>
          </Block>
        </div>
      </section>
    </main>
  );
};

export default Tietosuoja;
